// @ts-nocheck
// /day — "שאלת האריה היומית" prototype (29.9.2026).
// One situation a day, כן/לא, Shlomo's three-line answer, a streak kept in
// localStorage, and a share card sent through the phone's native share sheet
// (WhatsApp is one tap away). No login, nothing stored server-side yet.
// Query ?d=N opens a specific day for review; ?p=1 shows the draft badge.

import { useEffect, useMemo, useState } from "react";
import { LION_DAYS, dayIndexFor } from "../data/dailyLion";
import { supabase } from "../lib/supabase";

// Anonymous measurement (2.10.2026): one random id per device in localStorage,
// no login, no PII. Events land in daily_lion_events and feed the 09:00 report.
let firstSeen = false;
function anonId(): string {
  try {
    const k = "ei_lion_id";
    let v = window.localStorage.getItem(k);
    if (!v) {
      v = Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.localStorage.setItem(k, v);
      firstSeen = true;
    }
    return v;
  } catch {
    return "nols";
  }
}
function sourceTag(): string | null {
  try {
    const s = new URLSearchParams(window.location.search).get("s");
    if (s) {
      window.localStorage.setItem("ei_lion_src", s);
      return s;
    }
    return window.localStorage.getItem("ei_lion_src");
  } catch {
    return null;
  }
}
function logEvent(event: string, extra: Record<string, unknown> = {}) {
  try {
    const id = anonId();
    let ref: string | null = null;
    try {
      ref = document.referrer ? new URL(document.referrer).host : null;
    } catch {}
    // supabase-js builders are lazy: the request only fires once .then() runs.
    supabase
      .from("daily_lion_events")
      .insert({
        anon_id: id,
        event,
        src: sourceTag(),
        ref,
        first_seen: firstSeen && event === "view",
        ...extra,
      })
      .then(
        () => {},
        () => {}
      );
  } catch {}
}

const styles = `
.dl-wrap { direction: rtl; font-family: 'Heebo', Arial, sans-serif; max-width: 520px; margin: 0 auto; padding: 18px 16px 60px; min-height: 100vh; background: #F6F1E8; color: #2D1B10; }
.dl-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.dl-brand { font-family: 'Frank Ruhl Libre', Georgia, serif; font-size: 22px; font-weight: 700; }
.dl-brand small { display: block; font-family: 'Heebo', Arial, sans-serif; font-size: 12.5px; font-weight: 400; color: #8B7355; }
.dl-streak { background: #fff; border-radius: 999px; padding: 6px 12px; font-size: 14px; box-shadow: 0 1px 2px rgba(92,61,46,.08); white-space: nowrap; }
.dl-card { background: #fff; border-radius: 22px; padding: 22px 20px; box-shadow: 0 4px 18px rgba(92,61,46,.10); }
.dl-day { font-size: 12.5px; color: #8B7355; margin-bottom: 8px; }
.dl-line { font-size: 22px; line-height: 1.45; font-weight: 600; margin: 0 0 18px; }
.dl-q { font-family: 'Frank Ruhl Libre', Georgia, serif; font-size: 26px; font-weight: 700; margin: 0 0 14px; }
.dl-btns { display: flex; gap: 10px; }
.dl-btn { flex: 1; border: none; border-radius: 16px; padding: 16px; font-size: 20px; font-weight: 700; cursor: pointer; font-family: inherit; }
.dl-btn.yes { background: #F3D9C4; color: #6B3A1F; }
.dl-btn.no { background: #DDF2E4; color: #1F3A2A; }
.dl-verdict { font-family: 'Frank Ruhl Libre', Georgia, serif; font-size: 24px; font-weight: 700; margin: 6px 0 14px; }
.dl-ans p { margin: 0 0 12px; font-size: 16.5px; line-height: 1.65; }
.dl-ans b { color: #A0704A; }
.dl-share { margin-top: 18px; background: #F6F1E8; border-radius: 16px; padding: 14px; }
.dl-share-card { font-size: 16px; line-height: 1.55; margin: 0 0 12px; }
.dl-share-btn { width: 100%; border: none; border-radius: 14px; padding: 14px; font-size: 17px; font-weight: 700; color: #fff; background: linear-gradient(135deg, #25D366, #128C7E); cursor: pointer; font-family: inherit; }
.dl-copied { text-align: center; font-size: 13.5px; color: #1F3A2A; margin-top: 8px; }
.dl-book { margin-top: 22px; text-align: center; font-size: 14.5px; color: #5C3D2E; }
.dl-book a { color: #A0704A; font-weight: 700; }
.dl-offer { margin-top: 18px; background: #5C3D2E; color: #F6F1E8; border-radius: 18px; padding: 18px; text-align: center; }
.dl-offer a { display: inline-block; margin-top: 10px; background: #F0C27B; color: #2D1B10; border-radius: 12px; padding: 12px 18px; font-weight: 700; text-decoration: none; }
.dl-draft { display: inline-block; background: #FFE9A8; color: #6B4E00; border-radius: 8px; padding: 2px 8px; font-size: 12px; margin-inline-start: 8px; }
.dl-foot { margin-top: 26px; font-size: 12.5px; color: #8B7355; text-align: center; line-height: 1.6; }
.dl-nav { display: flex; justify-content: space-between; margin-top: 14px; font-size: 13px; }
.dl-nav a { color: #8B7355; }
`;

const BOOK_URL = "https://eiforall.com/b?utm_source=daily&utm_medium=game&utm_campaign=lion-day";
const SITE_URL = "https://eiforall.com/day";

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function readStreak(): { count: number; last: string; total: number } {
  try {
    const raw = window.localStorage.getItem("ei_lion_streak");
    if (raw) return JSON.parse(raw);
  } catch {}
  return { count: 0, last: "", total: 0 };
}

function bumpStreak() {
  const s = readStreak();
  const key = todayKey();
  if (s.last === key) return s;
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yKey = `${y.getFullYear()}-${y.getMonth() + 1}-${y.getDate()}`;
  const next = { count: s.last === yKey ? s.count + 1 : 1, last: key, total: (s.total || 0) + 1 };
  try {
    window.localStorage.setItem("ei_lion_streak", JSON.stringify(next));
  } catch {}
  return next;
}

export function DailyLionPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const forced = params.get("d");
  const preview = params.get("p") === "1";
  const idx = forced ? Math.max(0, Math.min(LION_DAYS.length - 1, parseInt(forced, 10) - 1 || 0)) : dayIndexFor(new Date());
  const day = LION_DAYS[idx];
  const [answered, setAnswered] = useState<null | boolean>(null);
  const [streak, setStreak] = useState(readStreak());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "יש כאן אריה? · שאלת האריה היומית";
    logEvent("view", { day_id: day.id, streak: streak.count, total: streak.total || 0 });
  }, []);

  const answer = (v: boolean) => {
    setAnswered(v);
    const s = bumpStreak();
    setStreak(s);
    logEvent("answer", { day_id: day.id, answer: v, correct: v === day.lion, streak: s.count, total: s.total || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const shareText = `🦁${day.lion ? "✅" : "❌"} יום ${streak.count || 1} ברצף. ${day.card}\nשאלת האריה היומית ← ${SITE_URL}?s=wa`;

  const share = async () => {
    logEvent("share", { day_id: day.id, streak: streak.count, total: streak.total || 0 });
    try {
      if (navigator.share) {
        await navigator.share({ text: shareText });
        return;
      }
    } catch {}
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  // Daily email (30.10.2026): the כן/לא buttons in the morning email link
  // here with ?a=yes|no, so the reader lands straight on Shlomo's answer.
  useEffect(() => {
    const a = params.get("a");
    if (answered === null && (a === "yes" || a === "no")) answer(a === "yes");
  }, []);

  const correct = answered === day.lion;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="dl-wrap">
        <div className="dl-top">
          <div className="dl-brand">
            יש כאן אריה?
            <small>שאלת האריה היומית · 20 שניות ביום</small>
          </div>
          <div className="dl-streak">🔥 {streak.count} ברצף</div>
        </div>

        <div className="dl-card">
          <div className="dl-day">
            יום {day.id}
            {preview && day.draft && <span className="dl-draft">טיוטה, ממתין לשלמה</span>}
          </div>
          <p className="dl-line">{day.line}</p>

          {answered === null ? (
            <>
              <h2 className="dl-q">יש כאן אריה?</h2>
              <div className="dl-btns">
                <button className="dl-btn yes" onClick={() => answer(true)}>כן</button>
                <button className="dl-btn no" onClick={() => answer(false)}>לא</button>
              </div>
            </>
          ) : (
            <>
              <div className="dl-verdict">
                {day.lion ? "כן." : "לא."} {day.lionNote ? day.lionNote.replace(/^לא, /, "") : correct ? "צדקת." : "והבטן אמרה כן, כמו אצל כולנו."}
              </div>
              <div className="dl-ans">
                <p><b>הרגש:</b> {day.feeling}</p>
                <p><b>מה קרה באמת:</b> {day.really}</p>
                <p><b>שלוש השניות:</b> {day.three}</p>
              </div>
              <div className="dl-share">
                <p className="dl-share-card">🦁{day.lion ? "✅" : "❌"} יום {streak.count} ברצף. {day.card}</p>
                <button className="dl-share-btn" onClick={share}>לשלוח לקבוצת המשפחה</button>
                {copied && <div className="dl-copied">הכרטיס הועתק. הדביקי בוואטסאפ.</div>}
              </div>
              {streak.total >= 7 ? (
                <div className="dl-offer">
                  שבוע שלם של שאלות. הסדנה המלאה, 12 מפגשים קצרים בקולו של שלמה, מחכה לך.
                  <br />
                  <a href={BOOK_URL} onClick={() => logEvent("book_click", { day_id: day.id, total: streak.total || 0 })}>לסדנה בספר · 89 ₪</a>
                </div>
              ) : (
                <div className="dl-book">
                  מחר שאלה חדשה. הסדנה המלאה: <a href={BOOK_URL} onClick={() => logEvent("book_click", { day_id: day.id, total: streak.total || 0 })}>«שאלת האריה», 12 מפגשים קצרים</a>
                </div>
              )}
            </>
          )}
        </div>

        {preview && (
          <div className="dl-nav">
            <a href={`/day?p=1&d=${Math.max(1, day.id - 1)}`}>← יום קודם</a>
            <a href={`/day?p=1&d=${Math.min(LION_DAYS.length, day.id + 1)}`}>יום הבא →</a>
          </div>
        )}

        <div className="dl-foot">
          שלמה אלפנבוים · «שאלת האריה - סדנה בספר»
          <br />
          התשובות הן של שלמה, מהחיים שלו. אין כאן ייעוץ ואין אבחון, יש שלוש שניות.
        </div>
      </div>
    </>
  );
}
